import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Plus, Search, Trash2, Edit, Star, Eye, Home } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { Skeleton } from '@/components/ui/skeleton';
import { useProperties } from '@/hooks/useProperties';
import { useDeleteProperty, useTogglePropertyFeatured } from '@/hooks/usePropertyMutations';
import { formatCurrency, formatDate } from '@/lib/format';
import { PROPERTY_TYPES, PROPERTY_STATUSES, PROPERTY_PURPOSES } from '@/types/property';
import { toast } from 'sonner';

export default function AdminPropertiesPage() {
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const { data, isLoading } = useProperties({ pageSize: 100 });
  const deleteProperty = useDeleteProperty();
  const toggleFeatured = useTogglePropertyFeatured();

  const properties = data?.properties || [];

  const filteredProperties = properties.filter((property) => {
    const matchesSearch = 
      property.title.toLowerCase().includes(search.toLowerCase()) ||
      property.city.toLowerCase().includes(search.toLowerCase());
    
    const matchesStatus = statusFilter === 'all' || property.status === statusFilter;
    
    return matchesSearch && matchesStatus;
  });

  const handleDelete = async (id: string) => {
    try {
      await deleteProperty.mutateAsync(id);
      toast.success('Imóvel excluído com sucesso!');
    } catch {
      toast.error('Erro ao excluir imóvel.');
    }
  };

  const handleToggleFeatured = async (id: string, currentValue: boolean) => {
    try {
      await toggleFeatured.mutateAsync({ id, is_featured: !currentValue });
      toast.success(currentValue ? 'Removido dos destaques!' : 'Adicionado aos destaques!');
    } catch {
      toast.error('Erro ao atualizar imóvel.');
    }
  };

  return (
    <div className="space-y-4 sm:space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-xl font-bold text-foreground sm:text-2xl">Imóveis</h1>
          <p className="text-sm text-muted-foreground sm:text-base">Gerencie seus imóveis cadastrados</p>
        </div>
        <Button asChild className="gap-2 w-full sm:w-auto">
          <Link to="/admin/imoveis/novo">
            <Plus className="h-4 w-4" />
            Novo Imóvel
          </Link>
        </Button>
      </div>

      {/* Filters */}
      <div className="flex flex-col gap-4 sm:flex-row">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Buscar por título ou cidade..."
            className="pl-9"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-full sm:w-48">
            <SelectValue placeholder="Filtrar por status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos os status</SelectItem>
            {Object.entries(PROPERTY_STATUSES).map(([value, label]) => (
              <SelectItem key={value} value={value}>{label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Properties List */}
      {isLoading ? (
        <div className="space-y-4">
          {[...Array(5)].map((_, i) => (
            <Skeleton key={i} className="h-24 w-full" />
          ))}
        </div>
      ) : filteredProperties.length > 0 ? (
        <div className="space-y-4">
          {filteredProperties.map((property) => (
            <Card key={property.id}>
              <CardContent className="p-3 sm:p-4">
                <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:gap-4">
                  {/* Image */}
                  <div className="h-32 w-full flex-shrink-0 overflow-hidden rounded-md bg-muted sm:h-20 sm:w-28">
                    {property.main_image_url ? (
                      <img 
                        src={property.main_image_url} 
                        alt={property.title}
                        className="h-full w-full object-cover"
                      />
                    ) : (
                      <div className="flex h-full w-full items-center justify-center">
                        <Home className="h-6 w-6 text-muted-foreground" />
                      </div>
                    )}
                  </div>

                  {/* Info */}
                  <div className="flex-1 min-w-0 space-y-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <h3 className="text-sm font-semibold text-foreground sm:text-base">{property.title}</h3>
                      {property.is_featured && (
                        <Badge className="bg-amber-100 text-amber-700 text-xs">
                          <Star className="mr-1 h-3 w-3" />
                          Destaque
                        </Badge>
                      )}
                    </div>
                    <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground sm:gap-4 sm:text-sm">
                      <span>{property.city}, {property.neighborhood}</span>
                      <span className="hidden sm:inline">{PROPERTY_TYPES[property.type]}</span>
                      <span className="hidden sm:inline">{PROPERTY_PURPOSES[property.purpose]}</span>
                      <span className="flex items-center gap-1">
                        <Eye className="h-3 w-3" />
                        {property.views_count}
                      </span>
                    </div>
                    <div className="flex flex-wrap items-center gap-2 sm:gap-4">
                      <span className="font-semibold text-primary">{formatCurrency(property.price)}</span>
                      <Badge variant={property.status === 'disponivel' ? 'default' : 'secondary'} className="text-xs">
                        {PROPERTY_STATUSES[property.status]}
                      </Badge>
                      <span className="text-xs text-muted-foreground">
                        {formatDate(property.created_at)}
                      </span>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-2">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleToggleFeatured(property.id, property.is_featured)}
                      className={property.is_featured ? 'text-amber-600' : 'text-muted-foreground'}
                    >
                      <Star className={`h-4 w-4 ${property.is_featured ? 'fill-current' : ''}`} />
                    </Button>
                    <Button variant="ghost" size="icon" asChild>
                      <Link to={`/admin/imoveis/${property.id}`}>
                        <Edit className="h-4 w-4" />
                      </Link>
                    </Button>
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button variant="ghost" size="icon" className="text-destructive">
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Excluir imóvel?</AlertDialogTitle>
                          <AlertDialogDescription>
                            Esta ação não pode ser desfeita. O imóvel será permanentemente removido.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Cancelar</AlertDialogCancel>
                          <AlertDialogAction onClick={() => handleDelete(property.id)}>
                            Excluir
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <Card>
          <CardContent className="py-12 text-center">
            <Home className="mx-auto mb-4 h-12 w-12 text-muted-foreground" />
            <p className="text-muted-foreground">Nenhum imóvel encontrado.</p>
            <Button asChild className="mt-4">
              <Link to="/admin/imoveis/novo">Cadastrar Primeiro Imóvel</Link>
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
